import { HttpClient, HttpParams, HttpResponse } from '@angular/common/http';
import { Injectable, Optional } from '@angular/core';
import { Observable, map } from 'rxjs';
import { environment } from '../../../environments/environment';
import { ModelDTO } from '../models/blower-model.model';
import { BlowerDTO, FormValues } from '../dto/blower-payload.dto';
import { Product } from '../models/product.model';

@Injectable({ providedIn: 'root' })
export class ProductRepository {
  constructor(@Optional() private http?: HttpClient) {}

  // Small helpers to consolidate http calls and reduce duplicated call sites
  private getRequest<T>(url: string, options?: any): Observable<T> {
    // Ensure we request the response body by default so the returned Observable<T>
    // matches callers' expected types. If a caller intentionally wants the full
    // response or events they should call HttpClient directly (see getDownloadPdf).
    if (!this.http) {
      throw new Error('HttpClient is required to perform HTTP requests in ProductRepository');
    }
    const callOptions: any = { ...(options || {}), observe: options?.observe ?? 'body' };
    return this.http.get<T>(url, callOptions) as Observable<T>;
  }

  private postRequest<T>(url: string, body: any, options?: any): Observable<T> {
    if (!this.http) {
      throw new Error('HttpClient is required to perform HTTP requests in ProductRepository');
    }
    const callOptions: any = { ...(options || {}), observe: options?.observe ?? 'body' };
    return this.http.post<T>(url, body, callOptions) as Observable<T>;
  }

  getProducts(): Observable<Product[]> {
    const url = environment.products.get;
    return this.http.get<Product[]>(url);
  }

  getBlowerTypes(): Observable<string> {
    const url = environment.blowersTypes.get;
    return this.http.get<string>(url);
  }
  fetchFields(payload: BlowerDTO[]): Observable<Map<string, object>> {
    const url = environment.products.fields.fetch;
    return this.postRequest<Map<string, object>>(url, payload);
  }

  fetchExactModels(models: any): Observable<ModelDTO[]> {
    const url = environment.products.exactModel;
    // models may include selectedUnits when provided by the caller
    return this.postRequest<ModelDTO[]>(url, models);
  }

  getAllModels(): Observable<string[]> {
    const url = environment.getAllModels.get;
    return this.getRequest<string[]>(url);
  }

  fetchSpecificModel(body: FormValues, specificModel: string): Observable<ModelDTO[]> {
    let url = environment.products.specificModel;
    url = url.replace('{specificModel}', specificModel);
    return this.postRequest<ModelDTO[]>(url, body);
  }
  fetchFormula(valueType: string, sourceUnit: string, targetUnit: string): Observable<string> {
    const url = environment.units.formulas;
    const params = new HttpParams().set('valueType', valueType).set('sourceUnit', sourceUnit).set('targetUnit', targetUnit);
    return this.http.get<any>(url, { params }).pipe(map((res) => res?.formula));
  }

  /**
   * Request a generated PDF for the provided model. Tests expect the url to contain 'generate-pdf'
   */
  getDownloadPdf(modelName: string, asAttachment: boolean, payload: any): Observable<HttpResponse<Blob>> {
    const url = `${environment.products.get}/generate-pdf/${modelName}`;
    // use observe: 'response' and responseType: 'blob' so callers get the full HttpResponse
    const options = { responseType: 'blob' as const, observe: 'response' as const } as const;
    return this.http.post(url, payload, options) as Observable<HttpResponse<Blob>>;
  }

  /**
   * Fetch suggested models map - added to match test helpers which expect this method to exist.
   */
  fetchSuggestedModels(exactModel?: any): Observable<Map<string, object>> {
    const url = `${environment.products.get}/suggested-models`;
    // If an exactModel is provided, post it to the suggested-models endpoint, otherwise GET
    if (exactModel) {
      return this.postRequest<Map<string, object>>(url, exactModel);
    }
    return this.getRequest<Map<string, object>>(url);
  }

  /**
   * Return a simple list of model names. Added to match global test stubs.
   */
  fetchModelNames(): Observable<string[]> {
    const url = `${environment.products.get}/model-names`;
    return this.getRequest<string[]>(url);
  }
}
